(function () {
  const ROOT_SELECTOR = '.customily-modal-preview-only';
  const TITLE_TEXT = 'Please check the preview carefully';
  const SUBTITLE_TEXT = 'Make sure the customization is correct to receive your item faster';
  const OPEN_ANIMATION_MS = 280;
  const CLOSE_ANIMATION_MS = 210;
  const CLICK_DRAG_THRESHOLD = 8;
  const SCROLL_LOCK_KEYS = new Set([' ', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', 'Home', 'End']);
  const CUSTOMILY_SELECTED_BORDER_COLOR = '#5e0d0d';
  const enhancingRoots = new WeakSet();
  let scanPending = false;
  let previewPendingTimer = 0;
  let previewScrollX = 0;
  let previewScrollY = 0;
  let shouldRestorePreviewScroll = false;
  let scrollRestoreTimers = [];
  let scrollRestoreReleaseTimer = 0;
  let previewBodyLock = null;
  let previewScrollGuardActive = false;
  let previewTouchY = 0;
  let customilyOptionsPointer = null;
  let suppressOptionsClickUntil = 0;
  let customilyValidationErrorsEnabled = false;
  let requiredValidationSyncPending = false;
  let selectedOptionBorderSyncPending = false;
  const CLOSE_ICON = `
    <svg class="icon icon-close" viewBox="0 0 18 17" focusable="false" aria-hidden="true">
      <path fill="currentColor" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"/>
    </svg>
  `;

  function isVisible(element) {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
  }

  function isPreviewScrollLocked() {
    return document.body.classList.contains('dm-customily-preview-pending') || document.body.classList.contains('dm-customily-preview-open');
  }

  function isIOSDevice() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  }

  function protectIOSCustomilyInput(target) {
    if (!isIOSDevice()) return;

    const control = target?.closest?.('#customily-options input, #customily-options textarea, #customily-options select, #customily-options [contenteditable="true"]');
    if (!control) return;

    control.style.setProperty('font-size', '16px', 'important');
  }

  function getEventElement(event) {
    return event.target instanceof Element ? event.target : event.target?.parentElement;
  }

  function getInteractionPoint(event) {
    const touch = event.touches?.[0] || event.changedTouches?.[0];
    const point = touch || event;

    if (typeof point.clientX !== 'number' || typeof point.clientY !== 'number') return null;

    return {
      x: point.clientX,
      y: point.clientY
    };
  }

  function rememberCustomilyOptionsPointer(event) {
    const target = getEventElement(event);
    if (!target?.closest('#customily-options')) return;

    const point = getInteractionPoint(event);
    if (!point) return;

    customilyOptionsPointer = {
      x: point.x,
      y: point.y
    };
  }

  function trackCustomilyOptionsPointer(event) {
    if (!customilyOptionsPointer) return;

    const point = getInteractionPoint(event);
    if (!point) return;

    const deltaX = Math.abs(point.x - customilyOptionsPointer.x);
    const deltaY = Math.abs(point.y - customilyOptionsPointer.y);

    if (Math.max(deltaX, deltaY) >= CLICK_DRAG_THRESHOLD) {
      suppressOptionsClickUntil = Date.now() + 450;
    }
  }

  function clearCustomilyOptionsPointer() {
    customilyOptionsPointer = null;
  }

  function isPreviewTrigger(target) {
    const trigger = target?.closest?.('#customily-preview-button');
    if (!trigger) return false;

    return !trigger.closest('#customily-options');
  }

  function getCustomilyOptionsRoot(control) {
    return control?.closest?.('#customily-options');
  }

  function escapeCustomilySelectorValue(value) {
    if (window.CSS?.escape) return CSS.escape(value);

    return String(value).replace(/["\\]/g, '\\$&');
  }

  function getCustomilyFieldWrapper(control) {
    return (
      control?.closest?.(
        '.customily-option, .customily_option, .customily-option-container, .customily_option_container, .customily-section, .customily_section, .customily-field, .customily_input, .form-group, .cl_option, li'
      ) ||
      control?.parentElement ||
      null
    );
  }

  function getCustomilyLabelText(control) {
    const root = getCustomilyOptionsRoot(control);
    const wrapper = getCustomilyFieldWrapper(control);
    const explicitLabel = control.id ? root?.querySelector(`label[for="${escapeCustomilySelectorValue(control.id)}"]`) : null;
    const label =
      explicitLabel ||
      control.closest('label') ||
      wrapper?.querySelector?.('label, .customily-option-label, .customily_label, .option-label, .form__label');

    if (!label) return '';

    const labelClone = label.cloneNode(true);
    labelClone.querySelectorAll('input, textarea, select, button, img, picture, canvas').forEach((node) => node.remove());

    return labelClone.textContent
      .replace(/\(\s*\d+\s*[|/]\s*\d+\s*\)/g, '')
      .replace(/\*/g, '')
      .replace(/\brequired\b/gi, '')
      .replace(/\s+/g, ' ')
      .replace(/[:：]\s*$/g, '')
      .trim();
  }

  function hasCustomilyRequiredMarker(control) {
    if (!control) return false;
    if (control.required || control.getAttribute('aria-required') === 'true') return true;
    if (/^(true|required|1)$/i.test(control.dataset.required || '')) return true;
    if (/required/i.test(`${control.dataset.validation || ''} ${control.className || ''}`)) return true;

    const wrapper = getCustomilyFieldWrapper(control);
    const labelText = wrapper
      ?.querySelector?.('label, .customily-option-label, .customily_label, .option-label, .form__label')
      ?.textContent?.trim();

    return Boolean(labelText && /(?:\*|\brequired\b)/i.test(labelText));
  }

  function isCustomilyRequiredControl(control) {
    if (!control || !getCustomilyOptionsRoot(control) || control.disabled) return false;

    const type = (control.type || '').toLowerCase();
    if (['button', 'submit', 'reset', 'hidden'].includes(type)) return false;
    if (!['radio', 'checkbox'].includes(type) && !isVisible(control)) return false;
    if (['radio', 'checkbox'].includes(type)) {
      const wrapper = getCustomilyFieldWrapper(control);
      const visibleChoice = wrapper?.querySelector?.('label, [role="radio"], [role="checkbox"], img, picture, canvas');
      if (!isVisible(control) && !isVisible(visibleChoice)) return false;
    }

    return hasCustomilyRequiredMarker(control);
  }

  function getCustomilyRadioGroup(control) {
    if (!control.name) return [control];

    const root = getCustomilyOptionsRoot(control);
    return Array.from(root.querySelectorAll(`input[type="${control.type}"][name="${escapeCustomilySelectorValue(control.name)}"]`)).filter(
      (input) => !input.disabled
    );
  }

  function getCustomilyChoiceGroupRoot(control) {
    const type = (control.type || '').toLowerCase();
    if (!['radio', 'checkbox'].includes(type)) return getCustomilyFieldWrapper(control);

    const root = getCustomilyOptionsRoot(control);
    const group = getCustomilyRadioGroup(control);
    const wrappers = group.map(getCustomilyFieldWrapper).filter(Boolean);
    const firstWrapper = wrappers[0] || getCustomilyFieldWrapper(control);

    if (!root || !firstWrapper) return firstWrapper;

    let candidate = firstWrapper;
    while (candidate && candidate !== root) {
      if (wrappers.every((wrapper) => candidate.contains(wrapper))) return candidate;
      candidate = candidate.parentElement;
    }

    return firstWrapper;
  }

  function getCustomilyChoiceGroupKey(control) {
    const type = (control.type || '').toLowerCase();
    return ['radio', 'checkbox'].includes(type) && control.name ? `${type}:${control.name}` : '';
  }

  function getCustomilyAssociatedLabel(control) {
    const root = getCustomilyOptionsRoot(control);
    if (!root) return null;

    const explicitLabel = control.id ? root.querySelector(`label[for="${escapeCustomilySelectorValue(control.id)}"]`) : null;
    return explicitLabel || control.closest('label') || getCustomilyFieldWrapper(control)?.querySelector?.('label');
  }

  function isCustomilyChoiceSelected(control) {
    if (control.checked) return true;
    if (getCustomilyChoiceGroupRoot(control)?.dataset.dmCustomilyChoiceSelected === 'true') return true;

    const label = getCustomilyAssociatedLabel(control);
    const wrapper = getCustomilyFieldWrapper(control);
    const selectedSignal = [label, wrapper].find((element) => {
      if (!element) return false;

      return (
        element.getAttribute('aria-checked') === 'true' ||
        element.getAttribute('aria-selected') === 'true' ||
        /\b(selected|active|checked|is-selected|is-active)\b/i.test(element.className || '')
      );
    });

    return Boolean(selectedSignal);
  }

  function hasCustomilyUploadedFileValue(control) {
    const root = getCustomilyOptionsRoot(control);
    const wrapper = getCustomilyFieldWrapper(control);
    if (!root || !wrapper) return false;

    const uploadWrapper = control.closest(
      '.dm-customily-upload-control-wrap, .customily-file-input, [data-testid^="image-upload-container-"]'
    ) || wrapper;

    if (
      uploadWrapper.classList.contains('dm-customily-upload-control-wrap--has-file') ||
      uploadWrapper.classList.contains('customily-file-with-thumbnail') ||
      uploadWrapper.querySelector(
        '.dm-customily-upload-summary, .customily-file-input-thumbnail, .customily-uploaded-image, .cl_remove-image, [class*="uploaded"] img, [class*="uploaded"] canvas'
      )
    ) {
      return true;
    }

    const requiredFileInputs = Array.from(root.querySelectorAll('input[type="file"]')).filter(hasCustomilyRequiredMarker);
    return requiredFileInputs.length === 1 && root.classList.contains('dm-customily-upload-has-file');
  }

  function hasCustomilyControlValue(control) {
    const tagName = control.tagName;
    const type = (control.type || '').toLowerCase();

    if (type === 'radio') return getCustomilyRadioGroup(control).some(isCustomilyChoiceSelected);
    if (type === 'checkbox') {
      const group = getCustomilyRadioGroup(control);
      return group.length > 1 ? group.some(isCustomilyChoiceSelected) : isCustomilyChoiceSelected(control);
    }
    if (type === 'file') return (control.files || []).length > 0 || hasCustomilyUploadedFileValue(control);
    if (tagName === 'SELECT') return Array.from(control.selectedOptions || []).some((option) => option.value.trim() !== '');

    return String(control.value || '').trim() !== '';
  }

  function getCustomilyConfiguredMessage(control) {
    const next = control.nextElementSibling;
    if (!next) return null;
    if (next.matches('input, textarea, select, button, label, .dm-customily-required-message')) return null;
    if (next.querySelector('input, textarea, select, button')) return null;

    return next.textContent.trim() ? next : null;
  }

  function getCustomilyValidationMessage(control) {
    const type = (control.type || '').toLowerCase();
    const isChoiceGroup = ['radio', 'checkbox'].includes(type);
    const wrapper = ['radio', 'checkbox'].includes(type) ? getCustomilyChoiceGroupRoot(control) : getCustomilyFieldWrapper(control);
    if (!wrapper) return null;

    const existingId = control.dataset.dmCustomilyRequiredMessage;
    if (existingId) {
      const existing = document.getElementById(existingId);
      if (existing) {
        if (isChoiceGroup && existing.parentElement !== wrapper) {
          wrapper.appendChild(existing);
        }

        existing.classList.toggle('dm-customily-required-message--choice', isChoiceGroup);
        return existing;
      }
    }

    let message = wrapper.querySelector('.dm-customily-required-message');

    if (!message) {
      message = getCustomilyConfiguredMessage(control) || document.createElement('div');

      if (!message.isConnected) {
        if (isChoiceGroup) {
          wrapper.appendChild(message);
        } else {
          control.insertAdjacentElement('afterend', message);
        }
      }
    }

    if (isChoiceGroup && message.parentElement !== wrapper) {
      wrapper.appendChild(message);
    }

    message.id = message.id || `dm-customily-required-${Math.random().toString(36).slice(2)}`;
    message.classList.add('dm-customily-required-message');
    message.classList.toggle('dm-customily-required-message--choice', isChoiceGroup);

    if (!message.textContent.trim()) {
      const label = getCustomilyLabelText(control);
      message.textContent = label ? `Please complete ${label}.` : 'Please complete this required field.';
    }

    control.dataset.dmCustomilyRequiredMessage = message.id;
    return message;
  }

  function setCustomilyValidationState(control, showError) {
    const invalid = !hasCustomilyControlValue(control);
    const shouldShow = customilyValidationErrorsEnabled && showError && invalid;
    const type = (control.type || '').toLowerCase();
    const wrapper = ['radio', 'checkbox'].includes(type) ? getCustomilyChoiceGroupRoot(control) : getCustomilyFieldWrapper(control);
    const message = getCustomilyValidationMessage(control);
    const group = ['radio', 'checkbox'].includes(type) ? getCustomilyRadioGroup(control) : [control];

    wrapper?.classList.toggle('dm-customily-required-invalid', shouldShow);
    message?.classList.toggle('dm-customily-required-message--visible', shouldShow);

    group.forEach((input) => {
      if (!input.dataset.dmCustomilyOriginalDescribedBy) {
        input.dataset.dmCustomilyOriginalDescribedBy = input.getAttribute('aria-describedby') || '';
      }

      if (shouldShow) {
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute(
          'aria-describedby',
          [input.dataset.dmCustomilyOriginalDescribedBy, message?.id].filter(Boolean).join(' ')
        );
      } else {
        input.removeAttribute('aria-invalid');
        if (input.dataset.dmCustomilyOriginalDescribedBy) {
          input.setAttribute('aria-describedby', input.dataset.dmCustomilyOriginalDescribedBy);
        } else {
          input.removeAttribute('aria-describedby');
        }
      }
    });

    return !invalid;
  }

  function hideCustomilyRequiredValidationErrors() {
    customilyValidationErrorsEnabled = false;

    document.querySelectorAll('#customily-options *').forEach((element) => {
      const text = element.textContent?.trim();
      if (text !== 'Please complete this required field.' && !/^Please complete .+\.$/.test(text || '')) return;
      if (element.querySelector?.('input, textarea, select, button, img, picture, canvas')) return;

      element.classList.add('dm-customily-required-message');
      element.classList.remove('dm-customily-required-message--visible');
    });

    document.querySelectorAll('#customily-options .dm-customily-required-invalid').forEach((wrapper) => {
      wrapper.classList.remove('dm-customily-required-invalid');
    });

    document.querySelectorAll('#customily-options .dm-customily-required-message').forEach((message) => {
      message.classList.remove('dm-customily-required-message--visible');
    });

    document.querySelectorAll('#customily-options [aria-invalid="true"]').forEach((control) => {
      control.removeAttribute('aria-invalid');

      if (control.dataset.dmCustomilyOriginalDescribedBy) {
        control.setAttribute('aria-describedby', control.dataset.dmCustomilyOriginalDescribedBy);
      } else {
        control.removeAttribute('aria-describedby');
      }
    });
  }

  function findCustomilyChoiceControlFromClick(target) {
    const root = target?.closest?.('#customily-options');
    if (!root) return null;

    const directControl = target.closest('input[type="radio"], input[type="checkbox"]');
    if (directControl) return directControl;

    const label = target.closest('label');
    if (label) {
      const nestedControl = label.querySelector('input[type="radio"], input[type="checkbox"]');
      if (nestedControl) return nestedControl;

      const labelFor = label.getAttribute('for');
      if (labelFor) {
        const explicitControl = root.querySelector(`#${escapeCustomilySelectorValue(labelFor)}`);
        if (explicitControl?.matches?.('input[type="radio"], input[type="checkbox"]')) return explicitControl;
      }
    }

    const wrapper = target.closest(
      '.customily-option, .customily_option, .customily-option-container, .customily_option_container, .customily-section, .customily_section, .cl_option, li, [role="radio"], [role="checkbox"]'
    );

    return wrapper?.querySelector?.('input[type="radio"], input[type="checkbox"]') || null;
  }

  function markCustomilyChoiceSelected(control) {
    if (!control) return;
    const type = (control.type || '').toLowerCase();
    if (!['radio', 'checkbox'].includes(type)) return;

    const groupRoot = getCustomilyChoiceGroupRoot(control);
    const groupKey = getCustomilyChoiceGroupKey(control);
    if (groupRoot) {
      groupRoot.dataset.dmCustomilyChoiceSelected = 'true';
      if (groupKey) groupRoot.dataset.dmCustomilyChoiceGroup = groupKey;
    }

    getCustomilyRadioGroup(control).forEach((input) => {
      delete input.dataset.dmCustomilyTouched;
      input.removeAttribute('aria-invalid');
    });

    setCustomilyValidationState(control, false);

    window.setTimeout(() => {
      setCustomilyValidationState(control, false);
    }, 0);
  }

  function getCustomilyRequiredControls() {
    const root = document.querySelector('#customily-options');
    if (!root) return [];

    const seenGroups = new Set();

    return Array.from(root.querySelectorAll('input, textarea, select')).filter((control) => {
      if (!isCustomilyRequiredControl(control)) return false;

      const type = (control.type || '').toLowerCase();
      const groupKey = ['radio', 'checkbox'].includes(type) && control.name ? `${type}:${control.name}` : '';
      if (groupKey && seenGroups.has(groupKey)) return false;
      if (groupKey) seenGroups.add(groupKey);

      return true;
    });
  }

  function syncCustomilyRequiredValidation() {
    getCustomilyRequiredControls().forEach((control) => {
      getCustomilyValidationMessage(control);
      setCustomilyValidationState(control, customilyValidationErrorsEnabled);
    });
  }

  function scheduleCustomilyRequiredValidationSync() {
    if (requiredValidationSyncPending) return;

    requiredValidationSyncPending = true;
    window.requestAnimationFrame(() => {
      requiredValidationSyncPending = false;

      syncCustomilyRequiredValidation();
      if (!customilyValidationErrorsEnabled) hideCustomilyRequiredValidationErrors();
    });
  }

  

  function scheduleCustomilySelectedOptionBorderSync() {
    if (selectedOptionBorderSyncPending) return;

    selectedOptionBorderSyncPending = true;
    window.requestAnimationFrame(() => {
      selectedOptionBorderSyncPending = false;
      
    });
  }

  function validateCustomilyRequiredOptions(showErrors) {
    customilyValidationErrorsEnabled = Boolean(showErrors);

    const firstInvalid = getCustomilyRequiredControls().find((control) => {
      if (showErrors) control.dataset.dmCustomilyTouched = 'true';
      return !setCustomilyValidationState(control, showErrors);
    });

    if (!firstInvalid) {
      customilyValidationErrorsEnabled = false;
      hideCustomilyRequiredValidationErrors();
      return true;
    }

    if (showErrors) {
      firstInvalid.focus?.({ preventScroll: true });
      firstInvalid.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }

    return false;
  }

  function markCustomilyCartRedirectIntent() {
    window.dmCustomilyCartRedirectUntil = Date.now() + 20000;
    document.documentElement.classList.add('dm-customily-cart-redirecting');
    document.body.classList.add('dm-customily-cart-redirecting');

    window.clearTimeout(window.dmCustomilyCartRedirectTimer);
    window.dmCustomilyCartRedirectTimer = window.setTimeout(() => {
      if (Number(window.dmCustomilyCartRedirectUntil || 0) > Date.now()) return;

      document.documentElement.classList.remove('dm-customily-cart-redirecting');
      document.body.classList.remove('dm-customily-cart-redirecting');
    }, 21000);
  }

  function isCustomilyCartTrigger(target) {
    return Boolean(
      target?.closest?.(
        '#customily-cart-btn, .dm-customily-preview__add, .customily-buttons-container .csh-button-atc, .customily-buttons-container .csh-button-atc button, .customily-buttons-container .csh-button-atc a'
      )
    );
  }

  function blockCustomilyActionWhenRequiredInvalid(event) {
    const target = getEventElement(event);
    if (!target?.closest?.('#customily-preview-button') && !isCustomilyCartTrigger(target)) return;
    if (!document.querySelector('#customily-options')) return;
    if (validateCustomilyRequiredOptions(true)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  }

  function rememberCustomilyCartRedirectIntent(event) {
    const target = getEventElement(event);
    if (!isCustomilyCartTrigger(target)) return;
    if (!document.querySelector('#customily-options')) return;
    if (!validateCustomilyRequiredOptions(true)) return;

    markCustomilyCartRedirectIntent();
  }

  function getScrollablePreviewElement(target, modal) {
    let node = target;

    while (node && node !== document.body) {
      if (node === modal || modal.contains(node)) {
        const style = window.getComputedStyle(node);
        const canScrollY = /(auto|scroll|overlay)/.test(style.overflowY) && node.scrollHeight > node.clientHeight + 1;

        if (canScrollY) return node;
      }

      if (node === modal) break;
      node = node.parentElement;
    }

    return null;
  }

  function canScrollPreviewElement(element, deltaY) {
    if (!element || !deltaY) return false;

    if (deltaY < 0) return element.scrollTop > 0;

    return element.scrollTop + element.clientHeight < element.scrollHeight - 1;
  }

  function shouldAllowPreviewScroll(event, deltaY) {
    const target = getEventElement(event);
    const modal = target?.closest('.dm-customily-preview');
    const scrollable = modal ? getScrollablePreviewElement(target, modal) : null;

    return canScrollPreviewElement(scrollable, deltaY);
  }

  function preventPreviewBackgroundWheel(event) {
    if (!isPreviewScrollLocked()) return;
    if (shouldAllowPreviewScroll(event, event.deltaY)) return;

    event.preventDefault();
    restorePreviewScrollPosition();
  }

  function rememberPreviewTouchStart(event) {
    if (!isPreviewScrollLocked() || event.touches.length !== 1) return;
    previewTouchY = event.touches[0].clientY;
  }

  function preventPreviewBackgroundTouch(event) {
    if (!isPreviewScrollLocked()) return;

    if (event.touches.length !== 1) {
      event.preventDefault();
      restorePreviewScrollPosition();
      return;
    }

    const nextY = event.touches[0].clientY;
    const deltaY = previewTouchY - nextY;
    previewTouchY = nextY;

    if (shouldAllowPreviewScroll(event, deltaY)) return;

    event.preventDefault();
    restorePreviewScrollPosition();
  }

  function preventPreviewKeyScroll(event) {
    if (!isPreviewScrollLocked()) return;
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
    if (!SCROLL_LOCK_KEYS.has(event.key)) return;
    if (getEventElement(event)?.closest('input, textarea, select, button, a, [contenteditable="true"], [role="button"]')) return;

    event.preventDefault();
    restorePreviewScrollPosition();
  }

  function enablePreviewScrollGuard() {
    if (previewScrollGuardActive) return;

    previewScrollGuardActive = true;
    document.addEventListener('wheel', preventPreviewBackgroundWheel, { capture: true, passive: false });
    document.addEventListener('touchstart', rememberPreviewTouchStart, { capture: true, passive: true });
    document.addEventListener('touchmove', preventPreviewBackgroundTouch, { capture: true, passive: false });
    document.addEventListener('keydown', preventPreviewKeyScroll, true);
  }

  function disablePreviewScrollGuard() {
    if (!previewScrollGuardActive) return;

    previewScrollGuardActive = false;
    document.removeEventListener('wheel', preventPreviewBackgroundWheel, true);
    document.removeEventListener('touchstart', rememberPreviewTouchStart, true);
    document.removeEventListener('touchmove', preventPreviewBackgroundTouch, true);
    document.removeEventListener('keydown', preventPreviewKeyScroll, true);
  }

  function findPreviewMedia(root) {
    return Array.from(root.querySelectorAll('img, canvas, video')).find((node) => {
      if (!isVisible(node)) return false;
      if (node.closest('[class*="thumb" i], [id*="thumb" i], [class*="loader" i]')) return false;

      const rect = node.getBoundingClientRect();
      const intrinsicWidth = node.naturalWidth || node.videoWidth || node.width || rect.width;
      const intrinsicHeight = node.naturalHeight || node.videoHeight || node.height || rect.height;

      return intrinsicWidth >= 160 && intrinsicHeight >= 160 && rect.width >= 120 && rect.height >= 120;
    });
  }

  function getMainProductSubmit() {
    return document.querySelector('form[data-type="add-to-cart-form"] [name="add"], product-form form [name="add"]');
  }

  function getExistingAddButton(root) {
    return Array.from(root.querySelectorAll('button, a')).find((button) => /add\s*to\s*cart/i.test(button.textContent || ''));
  }

  function getCloseCandidates(root) {
    return Array.from(
      root.querySelectorAll('button, a, [role="button"], [class*="close" i], [id*="close" i], [aria-label*="close" i], [title*="close" i]')
    ).filter((button) => {
      if (button.closest('.dm-customily-preview__header')) return false;

      const label = `${button.textContent || ''} ${button.getAttribute('aria-label') || ''} ${button.getAttribute('title') || ''}`;
      const classOrId = `${button.id || ''} ${button.className || ''}`;
      return /^(x|\u00d7)$/i.test((button.textContent || '').trim()) || /close|dismiss/i.test(label) || /close|dismiss/i.test(classOrId);
    });
  }

  function getExistingCloseButton(root) {
    return getCloseCandidates(root)[0];
  }

  function closeModal(root, closeButton) {
    if (!root || root.classList.contains('dm-customily-preview--closing')) return;

    root.classList.remove('dm-customily-preview--opening');
    root.classList.add('dm-customily-preview--closing');
    root.setAttribute('aria-hidden', 'true');

    window.setTimeout(() => {
      const shouldUseNativeClose =
        closeButton &&
        closeButton.isConnected &&
        closeButton.dataset.dmCustomilyCreatedClose !== 'true';

      if (shouldUseNativeClose) {
        closeButton.dataset.dmCustomilyBypassClose = 'true';
        closeButton.click();
        closeButton.dataset.dmCustomilyBypassClose = '';
        if (root.isConnected && isVisible(root)) {
          root.style.setProperty('display', 'none', 'important');
        }
      } else {
        root.style.setProperty('display', 'none', 'important');
      }

      if (root.isConnected) {
        root.classList.remove('dm-customily-preview--closing', 'dm-customily-preview--opening');
      }

      document.body.classList.remove('dm-customily-preview-open');
      unlockPreviewPagePosition();
      shouldRestorePreviewScroll = false;
      scrollRestoreTimers.forEach((timer) => window.clearTimeout(timer));
      scrollRestoreTimers = [];
      window.clearTimeout(scrollRestoreReleaseTimer);
    }, CLOSE_ANIMATION_MS);
  }

  function playOpenAnimation(root) {
    if (!root) return;

    root.classList.remove('dm-customily-preview--closing', 'dm-customily-preview--opening');
    root.removeAttribute('aria-hidden');

    window.requestAnimationFrame(() => {
      root.classList.add('dm-customily-preview--opening');
      window.setTimeout(() => {
        if (root.isConnected) root.classList.remove('dm-customily-preview--opening');
      }, OPEN_ANIMATION_MS);
    });
  }

  function bindClose(root, closeButton) {
    if (closeButton.dataset.dmCustomilyCloseBound === 'true') return;

    closeButton.dataset.dmCustomilyCloseBound = 'true';
    closeButton.addEventListener('click', (event) => {
      if (closeButton.dataset.dmCustomilyBypassClose === 'true') return;

      event.preventDefault();
      event.stopPropagation();
      closeModal(root, closeButton);
    });
  }

  function decorateCloseButton(closeButton) {
    if (!closeButton) return;
    if (closeButton.dataset.dmCustomilyDecoratedClose === 'true') return;

    closeButton.classList.add('dm-customily-preview__close');
    closeButton.setAttribute('aria-label', 'Close preview');
    closeButton.innerHTML = CLOSE_ICON;
    closeButton.dataset.dmCustomilyDecoratedClose = 'true';

    if (!closeButton.hasAttribute('type') && closeButton.tagName === 'BUTTON') {
      closeButton.type = 'button';
    }
  }

  function hideExtraCloseButtons(root, closeButton) {
    getCloseCandidates(root).forEach((candidate) => {
      if (candidate === closeButton) return;
      if (!candidate.classList.contains('dm-customily-preview__native-close-hidden')) {
        candidate.classList.add('dm-customily-preview__native-close-hidden');
      }
      if (candidate.getAttribute('aria-hidden') !== 'true') {
        candidate.setAttribute('aria-hidden', 'true');
      }
      if (candidate.getAttribute('tabindex') !== '-1') {
        candidate.setAttribute('tabindex', '-1');
      }
    });
  }

  function createCloseButton(panel) {
    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.dataset.dmCustomilyCreatedClose = 'true';
    panel.appendChild(closeButton);
    return closeButton;
  }

  function resetForcedHiddenState(root) {
    if (!root) return;

    if (root.style.getPropertyValue('display') === 'none') {
      root.style.removeProperty('display');
    }
  }

  function lockPreviewPagePosition() {
    if (previewBodyLock) return;

    previewScrollX = window.scrollX || window.pageXOffset || 0;
    previewScrollY = window.scrollY || window.pageYOffset || 0;
    shouldRestorePreviewScroll = true;
    enablePreviewScrollGuard();

    previewBodyLock = {
      scrollX: previewScrollX,
      scrollY: previewScrollY
    };
  }

  function unlockPreviewPagePosition() {
    if (!previewBodyLock) {
      disablePreviewScrollGuard();
      return;
    }

    const lock = previewBodyLock;
    previewBodyLock = null;

    window.scrollTo(lock.scrollX, lock.scrollY);
    disablePreviewScrollGuard();
  }

  function restorePreviewScrollPosition() {
    if (!shouldRestorePreviewScroll) return;

    const currentX = window.scrollX || window.pageXOffset || 0;
    const currentY = window.scrollY || window.pageYOffset || 0;
    const targetX = previewBodyLock?.scrollX ?? previewScrollX;
    const targetY = previewBodyLock?.scrollY ?? previewScrollY;

    if (Math.abs(currentX - targetX) > 1 || Math.abs(currentY - targetY) > 1) {
      window.scrollTo(targetX, targetY);
    }
  }

  function schedulePreviewScrollRestore() {
    scrollRestoreTimers.forEach((timer) => window.clearTimeout(timer));
    scrollRestoreTimers = [0, 60, 180, 360, 700, 1000, 1400, 2000, 2800].map((delay) => window.setTimeout(restorePreviewScrollPosition, delay));
    window.clearTimeout(scrollRestoreReleaseTimer);
    scrollRestoreReleaseTimer = window.setTimeout(() => {
      shouldRestorePreviewScroll = false;
      scrollRestoreTimers = [];
    }, 3200);
  }

  function markBodyOpen() {
    document.body.classList.add('dm-customily-preview-open');
  }

  function startPreviewPending() {
    lockPreviewPagePosition();
    document.body.classList.add('dm-customily-preview-pending');
    window.clearTimeout(previewPendingTimer);
    previewPendingTimer = window.setTimeout(stopPreviewPending, 2500);
    schedulePreviewScrollRestore();
  }

  function stopPreviewPending() {
    window.clearTimeout(previewPendingTimer);
    previewPendingTimer = 0;
    document.body.classList.remove('dm-customily-preview-pending');

    if (!document.body.classList.contains('dm-customily-preview-open')) {
      unlockPreviewPagePosition();
    }
  }

  function closeOpenModal() {
    const openModal = Array.from(document.querySelectorAll('.dm-customily-preview')).find(isVisible);
    if (!openModal) return false;

    closeModal(openModal, openModal.querySelector('.dm-customily-preview__close'));
    return true;
  }

  window.addEventListener('scroll', restorePreviewScrollPosition, { passive: true });

  function ensureOpenState(root, wasEnhanced) {
    markBodyOpen();
    stopPreviewPending();
    schedulePreviewScrollRestore();

    if (!wasEnhanced || root.classList.contains('dm-customily-preview--closing') || root.getAttribute('aria-hidden') === 'true') {
      playOpenAnimation(root);
      return;
    }

    root.removeAttribute('aria-hidden');
  }

  function ensurePanel(root, media) {
    const existingPanel = root.querySelector(':scope > .dm-customily-preview__panel');
    if (existingPanel) return existingPanel;

    let panel = media?.parentElement;
    while (panel && panel.parentElement !== root) {
      panel = panel.parentElement;
    }

    if (!panel || panel === media) {
      panel = document.createElement('div');
      panel.className = 'dm-customily-preview__panel';
      while (root.firstChild) panel.appendChild(root.firstChild);
      root.appendChild(panel);
      return panel;
    }

    panel.classList.add('dm-customily-preview__panel');
    return panel;
  }

  function ensureHeader(panel) {
    if (panel.querySelector('.dm-customily-preview__header')) return;

    const header = document.createElement('div');
    header.className = 'dm-customily-preview__header';
    header.innerHTML = `
      <h2 class="dm-customily-preview__title">${TITLE_TEXT}</h2>
      <p class="dm-customily-preview__subtitle">${SUBTITLE_TEXT}</p>
    `;
    panel.insertBefore(header, panel.firstChild);
  }

  function ensureClose(root, panel) {
    const closeButton = getExistingCloseButton(panel) || getExistingCloseButton(root) || createCloseButton(panel);

    decorateCloseButton(closeButton);
    hideExtraCloseButtons(root, closeButton);
    bindClose(root, closeButton);
  }

  function ensureAddToCart(panel) {
    let addButton = getExistingAddButton(panel);

    if (!addButton) {
      addButton = document.createElement('button');
      addButton.type = 'button';
      addButton.textContent = 'ADD TO CART';
      addButton.addEventListener('click', () => {
        const submit = getMainProductSubmit();
        if (submit && !submit.disabled && submit.getAttribute('aria-disabled') !== 'true') submit.click();
      });
      panel.appendChild(addButton);
    }

    addButton.classList.add('dm-customily-preview__add');
  }

  function enhanceModal(root) {
    if (!root) return;
    if (enhancingRoots.has(root)) return;

    enhancingRoots.add(root);

    try {
      const wasEnhanced = root.classList.contains('dm-customily-preview');

      if (wasEnhanced && !root.classList.contains('dm-customily-preview--closing')) {
        ensureOpenState(root, true);
        return;
      }

      const media = findPreviewMedia(root);
      if (!media) return;

      const panel = ensurePanel(root, media);
      resetForcedHiddenState(root);
      root.classList.add('dm-customily-preview');
      root.setAttribute('role', 'dialog');
      root.setAttribute('aria-modal', 'true');

      ensureHeader(panel);
      ensureClose(root, panel);
      ensureAddToCart(panel);

      media.classList.add('dm-customily-preview__media');
      ensureOpenState(root, wasEnhanced);
    } finally {
      enhancingRoots.delete(root);
    }
  }

  function scanForModals() {
    if (scanPending) return;
    scanPending = true;

    window.requestAnimationFrame(() => {
      scanPending = false;

      document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
        if (root.classList.contains('dm-customily-preview--closing')) return;
        if (isVisible(root)) enhanceModal(root);
      });
    });
  }

  function mutationTouchesPreviewModal(mutation) {
    const target = getEventElement({ target: mutation.target });
    if (target?.closest?.(ROOT_SELECTOR)) return true;

    return Array.from(mutation.addedNodes).some((node) => {
      const element = node instanceof Element ? node : node.parentElement;

      return element?.matches?.(ROOT_SELECTOR) || element?.querySelector?.(ROOT_SELECTOR);
    });
  }

  document.addEventListener('touchstart', rememberCustomilyOptionsPointer, { capture: true, passive: true });
  document.addEventListener('touchstart', (event) => protectIOSCustomilyInput(getEventElement(event)), { capture: true, passive: true });
  document.addEventListener('pointerdown', (event) => protectIOSCustomilyInput(getEventElement(event)), { capture: true, passive: true });
  document.addEventListener('focusin', (event) => protectIOSCustomilyInput(getEventElement(event)), true);
  document.addEventListener('touchmove', trackCustomilyOptionsPointer, { capture: true, passive: true });
  document.addEventListener('touchend', clearCustomilyOptionsPointer, { capture: true, passive: true });
  document.addEventListener('touchcancel', clearCustomilyOptionsPointer, { capture: true, passive: true });
  document.addEventListener('pointerdown', rememberCustomilyOptionsPointer, { capture: true, passive: true });
  document.addEventListener('pointermove', trackCustomilyOptionsPointer, { capture: true, passive: true });
  document.addEventListener('pointerup', clearCustomilyOptionsPointer, { capture: true, passive: true });
  document.addEventListener('pointercancel', clearCustomilyOptionsPointer, { capture: true, passive: true });

  document.addEventListener('focusout', (event) => {
    const target = getEventElement(event);
    if (!isCustomilyRequiredControl(target)) return;

    hideCustomilyRequiredValidationErrors();
    setCustomilyValidationState(target, false);
  }, true);

  document.addEventListener('input', (event) => {
    const target = getEventElement(event);
    if (!isCustomilyRequiredControl(target)) return;

    hideCustomilyRequiredValidationErrors();
    delete target.dataset.dmCustomilyTouched;
    setCustomilyValidationState(target, false);
  }, true);

  document.addEventListener('change', (event) => {
    const target = getEventElement(event);
    if (!isCustomilyRequiredControl(target)) return;

    hideCustomilyRequiredValidationErrors();
    markCustomilyChoiceSelected(target);
    delete target.dataset.dmCustomilyTouched;
    setCustomilyValidationState(target, false);
  }, true);

  document.addEventListener('click', (event) => {
    const control = findCustomilyChoiceControlFromClick(getEventElement(event));
    if (!control || !isCustomilyRequiredControl(control)) return;

    hideCustomilyRequiredValidationErrors();
    markCustomilyChoiceSelected(control);
    setCustomilyValidationState(control, false);
  }, true);

  document.addEventListener('click', blockCustomilyActionWhenRequiredInvalid, true);
  document.addEventListener('click', rememberCustomilyCartRedirectIntent, true);

  document.addEventListener('submit', (event) => {
    const root = document.querySelector('#customily-options');
    const form = event.target;
    const isProductForm =
      root &&
      form instanceof HTMLFormElement &&
      (form.contains(root) || form.closest('product-form') || form.matches('form[data-type="add-to-cart-form"]') || form.querySelector('[name="add"]'));

    if (!root || !isProductForm) return;
    if (validateCustomilyRequiredOptions(true)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  document.addEventListener('click', (event) => {
    const target = getEventElement(event);
    if (!isPreviewTrigger(target)) return;
    if (Date.now() <= suppressOptionsClickUntil) return;

    startPreviewPending();
    document.querySelectorAll(ROOT_SELECTOR).forEach(resetForcedHiddenState);
    [0, 16, 40, 80, 140, 220, 360, 700, 1200].forEach((delay) => window.setTimeout(scanForModals, delay));
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    const openModal = Array.from(document.querySelectorAll('.dm-customily-preview')).find(isVisible);
    if (!openModal) return;

    event.preventDefault();
    closeOpenModal();
  });

  if (window.MutationObserver) {
    const observer = new MutationObserver((mutations) => {
      if (!isPreviewScrollLocked() && !mutations.some(mutationTouchesPreviewModal)) return;

      scanForModals();
    });
    observer.observe(document.documentElement, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class', 'style', 'aria-hidden']
    });

    const requiredValidationObserver = new MutationObserver(() => {
      scheduleCustomilyRequiredValidationSync();
      scheduleCustomilySelectedOptionBorderSync();
    });
    requiredValidationObserver.observe(document.documentElement, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['required', 'aria-required', 'aria-invalid', 'data-required', 'class', 'style']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      scanForModals();
      syncCustomilyRequiredValidation();
      
    });
  } else {
    scanForModals();
    syncCustomilyRequiredValidation();
    
  }

  window.addEventListener('load', () => {
    syncCustomilyRequiredValidation();
    
  });

})();

(function () {
  const STICKY_SELECTOR = '.sticky-btn-container';
  const MOBILE_QUERY = '(max-width: 749px)';
  const mobileMedia = window.matchMedia?.(MOBILE_QUERY);

  let syncFrame = 0;

  function shouldHideCustomilyPreview() {
    return document.documentElement.classList.contains('dm-hide-customily-preview') ||
      document.body?.classList.contains('dm-hide-customily-preview') ||
      Boolean(document.querySelector('product-info.dm-hide-customily-preview'));
  }

  function hideCustomilyPreviewButtons() {
    document.querySelectorAll('#customily-preview-button, .customily-preview-button').forEach((button) => {
      button.style.setProperty('display', 'none', 'important');
      button.style.setProperty('visibility', 'hidden', 'important');
      button.style.setProperty('opacity', '0', 'important');
      button.style.setProperty('pointer-events', 'none', 'important');
      button.setAttribute('aria-hidden', 'true');
    });

    document.querySelectorAll(STICKY_SELECTOR).forEach((container) => {
      const isPreviewSticky = Boolean(container.querySelector('.customily-preview-button, #customily-preview-button')) ||
        /preview\s+your\s+personalization/i.test(container.textContent || '');
      if (!isPreviewSticky) return;

      container.style.setProperty('display', 'none', 'important');
      container.style.setProperty('visibility', 'hidden', 'important');
      container.style.setProperty('opacity', '0', 'important');
      container.style.setProperty('pointer-events', 'none', 'important');
      container.setAttribute('aria-hidden', 'true');
    });
  }

  function isMobile() {
    return mobileMedia ? mobileMedia.matches : window.innerWidth <= 749;
  }

  function forceStickyPreviewVisible() {
    syncFrame = 0;
    if (shouldHideCustomilyPreview()) {
      hideCustomilyPreviewButtons();
      return;
    }

    if (!isMobile()) return;

    document.querySelectorAll(STICKY_SELECTOR).forEach((container) => {
      container.style.setProperty('position', 'fixed', 'important');
      container.style.setProperty('left', '0', 'important');
      container.style.setProperty('right', '0', 'important');
      container.style.setProperty('bottom', '0', 'important');
      container.style.setProperty('z-index', '1000000', 'important');
      container.style.setProperty('display', 'flex', 'important');
      container.style.setProperty('visibility', 'visible', 'important');
      container.style.setProperty('opacity', '1', 'important');
      container.style.setProperty('pointer-events', 'auto', 'important');
      container.style.setProperty('transform', 'none', 'important');
    });
  }

  function scheduleStickyPreviewVisibilitySync() {
    if (syncFrame) window.cancelAnimationFrame(syncFrame);
    syncFrame = window.requestAnimationFrame(forceStickyPreviewVisible);
  }

  if (window.MutationObserver) {
    new MutationObserver(scheduleStickyPreviewVisibilitySync).observe(document.documentElement, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden']
    });
  }

  document.addEventListener('DOMContentLoaded', scheduleStickyPreviewVisibilitySync);
  window.addEventListener('load', scheduleStickyPreviewVisibilitySync);
  window.addEventListener('resize', scheduleStickyPreviewVisibilitySync);
  window.addEventListener('scroll', scheduleStickyPreviewVisibilitySync, { passive: true });
  mobileMedia?.addEventListener?.('change', scheduleStickyPreviewVisibilitySync);
  scheduleStickyPreviewVisibilitySync();
})();
